import {useState, useEffect} from 'react';
import {httpsCallable} from 'firebase/functions';
import {collection, getDocs, orderBy, query, limit} from 'firebase/firestore';
import {FeedbackList} from '../components/FeedbackList';
import {FeedbackFilters} from '../components/FeedbackFilters';
import {useFeedbackList} from '../hooks/useFeedback';
import {useAppVersions} from '../hooks/useAppVersions';
import {functions, db} from '../services/firebase';
import {House} from '@phosphor-icons/react';

export function DashboardPage() {
  const {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    filters,
    updateFilters,
    clearFilters,
  } = useFeedbackList();

    const {versions: appVersions} = useAppVersions();

    // Backfill AI Analysis
    const [backfillRemaining, setBackfillRemaining] = useState<number | null>(null);
    const [backfillRunning, setBackfillRunning] = useState(false);
    const [backfillProcessed, setBackfillProcessed] = useState(0);

    useEffect(() => {
      // Check how many feedbacks lack aiAnalysis
      const q2 = query(collection(db, 'feedback'), orderBy('createdAt', 'desc'), limit(500));
      getDocs(q2).then(snap => {
        const missing = snap.docs.filter(d => d.data().message && !d.data().aiAnalysis).length;
        setBackfillRemaining(missing > 0 ? missing : null);
      }).catch(() => {});
    }, []);

    const runBackfill = async () => {
      setBackfillRunning(true);
      const callable = httpsCallable<Record<string, never>, {processed: number; remaining: number}>(functions, 'backfillAiAnalysis', {timeout: 300000});
      let totalProcessed = 0;
      let remaining = backfillRemaining || 0;
      while (remaining > 0) {
        try {
          const result = await callable({});
          totalProcessed += result.data.processed;
          remaining = result.data.remaining;
          setBackfillProcessed(totalProcessed);
          setBackfillRemaining(remaining > 0 ? remaining : null);
          if (result.data.processed === 0) break;
        } catch (err) {
          console.error('Backfill batch error (retrying):', err);
          // Short pause then retry — the function may have timed out
          await new Promise(r => setTimeout(r, 2000));
        }
      }
      setBackfillRunning(false);
    };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-medium text-gray-900 flex items-center gap-2">
              <House size={26} weight="regular" className="text-primary"/>
              Home
          </h1>
          <div className="flex items-center gap-2">
              {backfillRemaining != null && (
                <button
                  onClick={runBackfill}
                  disabled={backfillRunning}
                  className="px-4 py-2 border border-violet-300 rounded-pill shadow-bo text-sm font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 disabled:opacity-50 transition-colors"
                >
                  {backfillRunning ? (
                    <>
                      <span className="inline-block w-3.5 h-3.5 border-2 border-violet-400 border-t-transparent rounded-full animate-spin mr-2"/>
                      {backfillProcessed} done, {backfillRemaining} left
                    </>
                  ) : `Backfill AI Analysis (${backfillRemaining})`}
                </button>
              )}
              <button
                  onClick={refresh}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded-pill shadow-bo text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-colors"
              >
                  Refresh
              </button>
          </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <FeedbackFilters
        filters={filters}
        onFilterChange={updateFilters}
        onClearFilters={clearFilters}
      />

      <FeedbackList
        items={items}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        appVersions={appVersions}
        onRefresh={refresh}
      />
    </div>
  );
}
